import { useEffect, useRef } from "react";
import type { KeyboardShortcut } from "../types/keyboard-shortcuts";

const activeShortcuts = new Map<string, KeyboardShortcut>();

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    (el as HTMLElement).isContentEditable
  );
}

function matchesShortcut(
  e: KeyboardEvent,
  shortcut: KeyboardShortcut,
): boolean {
  if (
    e.key.toLowerCase() !== shortcut.key.toLowerCase() &&
    e.key !== shortcut.key
  )
    return false;

  const mods = shortcut.modifiers ?? [];
  if (mods.includes("Meta") !== e.metaKey) return false;
  if (mods.includes("Control") !== e.ctrlKey) return false;
  if (mods.includes("Shift") !== e.shiftKey) return false;
  if (mods.includes("Alt") !== e.altKey) return false;

  return true;
}

/**
 * Registers a keyboard shortcut globally.
 * Automatically unregisters on unmount.
 */
export function useKeyboardShortcut(shortcut: KeyboardShortcut) {
  const handlerRef = useRef(shortcut.handler);
  handlerRef.current = shortcut.handler;

  useEffect(() => {
    const entry: KeyboardShortcut = {
      ...shortcut,
      handler: (e) => handlerRef.current(e),
    };
    activeShortcuts.set(shortcut.id, entry);

    return () => {
      activeShortcuts.delete(shortcut.id);
    };
  }, [shortcut.id, shortcut.key]);
}

/**
 * Global keyboard event listener — initialized once at app root.
 * Dispatches to registered shortcuts.
 */
export function useKeyboardShortcutListener() {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      for (const shortcut of activeShortcuts.values()) {
        if (!matchesShortcut(e, shortcut)) continue;

        // Suppress in inputs unless explicitly opted out
        if (shortcut.suppressInInputs !== false && isInputFocused()) continue;

        e.preventDefault();
        shortcut.handler(e);
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
}
